import { supabase } from "@/integrations/supabase/client";

// Get or create a persistent session ID for anonymous tracking
const getOrCreateSessionId = (): string => {
  let sessionId = localStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    localStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Get current user ID if authenticated
const getUserId = async (): Promise<string | null> => {
  const { data: { user } } = await supabase.auth.getUser();
  return user?.id || null;
};

// Track affiliate link clicks
export const trackAffiliateClick = async (
  cardId: string,
  clickSource: 'grid_view' | 'table_view' | 'detail_page' | 'assistant' | 'article_sidebar',
  cardName?: string,
  redirectShown?: boolean,
  redirectCancelled?: boolean,
  redirectDelayMs?: number
) => {
  try {
    const sessionId = getOrCreateSessionId();
    const userId = await getUserId();
    const referrer = document.referrer || null;

    const { error } = await supabase.from('affiliate_clicks').insert({
      card_id: cardId,
      click_source: clickSource,
      user_id: userId,
      session_id: sessionId,
      referrer,
    });

    if (error) {
      console.error('Error tracking affiliate click:', error);
    } else {
      console.log(`Tracked affiliate click: ${cardName || cardId} from ${clickSource}${redirectShown ? ' (redirect shown)' : ''}`);
    }
  } catch (error) {
    console.error('Error in trackAffiliateClick:', error);
  }
};

// Track AI assistant session start
export const trackAssistantStart = async (customSessionId?: string) => {
  try {
    const sessionId = customSessionId || getOrCreateSessionId();
    const userId = await getUserId();

    const { error } = await supabase.from('assistant_sessions').insert({
      session_id: sessionId,
      user_id: userId,
    });

    if (error) {
      console.error('Error tracking assistant start:', error);
    } else {
      console.log('Tracked assistant session start');
    }
  } catch (error) {
    console.error('Error in trackAssistantStart:', error);
  }
};

// Track AI assistant session completion
export const trackAssistantComplete = async (
  sessionId: string,
  answers: Record<string, string>,
  recommendations: string[]
) => {
  try {
    const { error } = await supabase
      .from('assistant_sessions')
      .update({
        completed_at: new Date().toISOString(),
        questions_answered: answers,
        cards_recommended: recommendations,
      })
      .eq('session_id', sessionId);

    if (error) {
      console.error('Error tracking assistant completion:', error);
    } else {
      console.log('Tracked assistant session completion');
    }
  } catch (error) {
    console.error('Error in trackAssistantComplete:', error);
  }
};

// Track card click from AI assistant recommendations
export const trackAssistantCardClick = async (
  sessionId: string,
  cardId: string
) => {
  try {
    // Get current cards_clicked array
    const { data: session } = await supabase
      .from('assistant_sessions')
      .select('cards_clicked')
      .eq('session_id', sessionId)
      .single();

    const currentClicks = (session?.cards_clicked as string[]) || [];
    const updatedClicks = [...currentClicks, cardId];

    const { error } = await supabase
      .from('assistant_sessions')
      .update({
        cards_clicked: updatedClicks,
      })
      .eq('session_id', sessionId);

    if (error) {
      console.error('Error tracking assistant card click:', error);
    } else {
      console.log(`Tracked assistant card click: ${cardId}`);
    }
  } catch (error) {
    console.error('Error in trackAssistantCardClick:', error);
  }
};
